import { Table, tokens } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { useNavigation } from '@hotjar/navigation';
import { formatPercentage, useTranslation } from '@hotjar/translations';
import {
  CenteredCell,
  SmallCell,
  SmallHeadCell,
  StyledRow,
  useAnswersTableRowData,
  useHighestValue,
} from './AnswersTable';
import type { SurveyQuestion } from '../../../../domain/Surveys/Questions';
import { QuestionTypes } from '../../../../domain/Surveys/Questions';
import type { AggregatedResult } from '../../../../domain/Surveys/Results/AggregatedResult';

const RatingLabelSpan = styled.span`
  ${tokens.text('caption')}
  color: ${tokens.color.text_light};
  margin-left: ${tokens.spacer.x1};
`;

const RatingLabel = ({ rating, question }: { rating: string; question: SurveyQuestion }) => {
  const getLabel = () => {
    switch (question.type) {
      case QuestionTypes.RATING_SCALE_5:
        if (rating === '1') return question.labels[0];
        if (rating === '5') return question.labels[1];
        break;
      case QuestionTypes.RATING_SCALE_7:
        if (rating === '1') return question.labels[0];
        if (rating === '7') return question.labels[1];
        break;
      default:
        throw new Error('Unexpected question type');
    }
    return undefined;
  };

  const label = getLabel();

  if (label === undefined) return null;

  return <RatingLabelSpan>{label.text}</RatingLabelSpan>;
};

export interface RatingScaleAnswersTableProps {
  question: SurveyQuestion;
  result: AggregatedResult;
  isSticky?: boolean;
}

export const RatingScaleAnswersTable = ({
  question,
  result,
  isSticky = true,
}: RatingScaleAnswersTableProps): JSX.Element | null => {
  const { t, i18n } = useTranslation('feedback');

  const [rowData, getSortDirection, getOnSort] = useAnswersTableRowData(result, 'answer');

  const highestValue = useHighestValue(result);

  const { topBarHeight: topNavHeight } = useNavigation();

  const caption = t('surveys.answer-table.caption', { count: result.total });

  return (
    <Table
      caption={caption}
      tableName={t('surveys.answer-table.name')}
      withStickyLastColumn={false}
    >
      <Table.Head>
        <Table.HeadRow isSticky={isSticky} stickyOffset={topNavHeight}>
          <Table.HeadCell
            isSortable
            title={t('surveys.answer-table.answer')}
            sortDirection={getSortDirection('answer')}
            onSort={getOnSort('answer')}
          >
            {t('surveys.answer-table.answer')}
          </Table.HeadCell>
          <SmallHeadCell
            isSortable
            title={t('surveys.answer-table.count')}
            sortDirection={getSortDirection('count')}
            onSort={getOnSort('count')}
          >
            {t('surveys.answer-table.count')}
          </SmallHeadCell>
          <SmallHeadCell
            isSortable
            title={t('surveys.answer-table.percentage')}
            sortDirection={getSortDirection('percent')}
            onSort={getOnSort('percent')}
          >
            {t('common:percent-sign')}
          </SmallHeadCell>
        </Table.HeadRow>
      </Table.Head>
      <Table.Body>
        <>
          {rowData.map((row) => {
            return (
              <StyledRow
                key={row.question}
                isHighlighted={highestValue === row.count}
                aria-label={
                  highestValue === row.count ? t('surveys.answer-table.most-frequent') : undefined
                }
              >
                <Table.Cell>
                  <>
                    {row.answer}
                    <RatingLabel rating={row.answer} question={question} />
                  </>
                </Table.Cell>
                <SmallCell>{row.count.toLocaleString(i18n.language)}</SmallCell>
                <SmallCell>{formatPercentage(Math.round(row.percent), i18n)}</SmallCell>
              </StyledRow>
            );
          })}
        </>
      </Table.Body>
      <Table.Foot>
        <StyledRow>
          <CenteredCell colSpan={3}>{caption}</CenteredCell>
        </StyledRow>
      </Table.Foot>
    </Table>
  );
};
